import { createContext, useCallback, useContext, useMemo } from 'react';
import { useAcceptAIFields, useDismissAIFields } from '../../../../../services/hooks/captable';

export type AIFieldAcceptContextType = {
  acceptField: (securityId: number, fieldName: string) => Promise<void>;
  acceptAllFields: (securityId: number) => Promise<void>;
  dismissField: (securityId: number, fieldName: string) => Promise<void>;
  dismissAllFields: (securityId: number) => Promise<void>;
};

const AIFieldAcceptContext = createContext<AIFieldAcceptContextType>({
  acceptField: async () => {},
  acceptAllFields: async () => {},
  dismissField: async () => {},
  dismissAllFields: async () => {},
});

export const useAIFieldAcceptContext = () => useContext(AIFieldAcceptContext);

interface AIFieldAcceptProviderProps {
  children: React.ReactNode;
  onDismissAiColumn?: (securityId: number) => void;
}

export const AIFieldAcceptProvider = ({ children, onDismissAiColumn }: AIFieldAcceptProviderProps) => {
  const { acceptAIFields } = useAcceptAIFields();
  const { dismissAIFields } = useDismissAIFields();

  const acceptField = useCallback(
    async (securityId: number, fieldName: string) => {
      await acceptAIFields(securityId, { field_names: [fieldName] });
    },
    [acceptAIFields]
  );

  const acceptAllFields = useCallback(
    async (securityId: number) => {
      await acceptAIFields(securityId, { all: true });
    },
    [acceptAIFields]
  );

  const dismissField = useCallback(
    async (securityId: number, fieldName: string) => {
      await dismissAIFields(securityId, { field_names: [fieldName] });
      if (fieldName === 'name') {
        onDismissAiColumn?.(securityId);
      }
    },
    [dismissAIFields, onDismissAiColumn]
  );

  const dismissAllFields = useCallback(
    async (securityId: number) => {
      await dismissAIFields(securityId, { all: true });
      onDismissAiColumn?.(securityId);
    },
    [dismissAIFields, onDismissAiColumn]
  );

  const aiFieldAcceptContextValue: AIFieldAcceptContextType = useMemo(() => ({ acceptField, acceptAllFields, dismissField, dismissAllFields }), [acceptField, acceptAllFields, dismissField, dismissAllFields]);

  return (
    <AIFieldAcceptContext.Provider value={aiFieldAcceptContextValue}>
      {children}
    </AIFieldAcceptContext.Provider>
  );
};

export default AIFieldAcceptContext;
